import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useSocialShareAccessDeniedStep from 'containers/SocialShareModal/SocialShareAccessDeniedStep';
import { useSocialShareModal } from 'containers/SocialShareModal/SocialShareModalContext';
import useOnShare from 'containers/SocialShareModal/useOnShare';
import useLinkedinAuthenticator from 'hooks/useLinkedinAuthenticator';
import {
  scheduleLinkedinVideo,
  shareLinkedinVideo,
} from 'redux/modules/download';
import {
  linkedinOrganizationsSelector,
  linkedinSelector,
} from 'redux/modules/social';
import { Dispatch } from 'redux/types';
import { SocialSharePlatformData } from '../types';
import {
  useLinkedInPostComposerStep,
  useLinkedInSwitchAccountStep,
} from './steps';
import { LinkedInSocialShareStep } from './types';

export default function useLinkedInSocialShare(): SocialSharePlatformData<
  LinkedInSocialShareStep
> {
  const { providerUserId, profileImageUrl, username, postStatus } = useSelector(
    linkedinSelector,
  );
  const organizations = useSelector(linkedinOrganizationsSelector) ?? [];

  const dispatch = useDispatch<Dispatch>();

  const {
    selectedAccountId,
    description,
    onAuthSuccess,
    onAuthError,
    goToStep,
  } = useSocialShareModal();

  const { authenticating, authenticate } = useLinkedinAuthenticator({
    force: true,
    onAuthSuccess: () => {
      onAuthSuccess();
      goToStep('composer');
    },
    onError: error => {
      onAuthError(error);
      goToStep('access-denied');
    },
  });

  const handleShare = useOnShare({
    shareNow: useCallback(
      () => dispatch(shareLinkedinVideo(selectedAccountId, description)),
      [description, dispatch, selectedAccountId],
    ),
    schedule: useCallback(
      (scheduledDate: number) =>
        dispatch(
          scheduleLinkedinVideo(selectedAccountId, description, scheduledDate),
        ),
      [description, dispatch, selectedAccountId],
    ),
  });

  const linkedInAccessDeniedStep = useSocialShareAccessDeniedStep({
    onCancel: () => goToStep('composer'),
  });

  const linkedInPostComposerStep = useLinkedInPostComposerStep({
    onShare: handleShare,
  });

  const linkedInSwitchAccountStep = useLinkedInSwitchAccountStep({
    authenticate,
  });

  return {
    platformAlias: 'linkedin',
    authenticating: authenticating || !username,
    accounts: [
      {
        id: providerUserId,
        avatarSrc: profileImageUrl,
        username,
        handle: username,
      },
      ...organizations?.map(organization => ({
        id: organization.id,
        avatarSrc: organization.logoImageUrl,
        username: organization.name,
        handle: organization.name,
      })),
    ],
    steps: [
      linkedInAccessDeniedStep,
      linkedInPostComposerStep,
      linkedInSwitchAccountStep,
    ],
    defaultStep: linkedInPostComposerStep.id,
    authenticate,
    postStatus,
    onSwitchAccount: () => goToStep('switch-account'),
  };
}
